import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";

import { updateDemographics } from "../../../actions/userActions";
import { demographicsOutstanding } from "../../../constants/demographicOptions";

import "../../../screens/About/aboutyou.css";

const SESSION_DISMISS_KEY = "nwd_about_you_dismissed";

/**
 * The Wave 2 demographics invitation, offered on /today.
 *
 * Shown only once the practice has returned something — product principle 2
 * rules out asking before then. The proxy for "returned something" is a second
 * saved day entry: day records are only created on submit, so two of them means
 * the user wrote, left, and chose to come back. (M-04's own "≥1 scored moment"
 * definition is not usable client-side — day content is an encrypted blob.)
 *
 * A card, never a modal. The ritual screen is not the place for an interruption.
 */
export default function AboutYouCard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const dayList = useSelector((state) => state.dayList);
  const { days } = dayList || {};

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === "true";
    } catch (err) {
      return false;
    }
  });

  const hasPractice = Array.isArray(days) && days.length >= 2;
  const outstanding =
    !!userInfo && demographicsOutstanding(userInfo.demographic);
  const visible = hasPractice && outstanding && !dismissed;

  useEffect(() => {
    if (visible) {
      ReactGA.event("demographics_prompted", { wave: 2, surface: "card" });
    }
  }, [visible]);

  if (!visible) return null;

  // "Not now" is per-session — the user may simply be mid-entry. "Don't ask
  // again" is permanent and is recorded as a decline, because a response rate
  // without a decline count cannot be read for bias.
  const notNow = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "true");
    } catch (err) {
      /* private mode — the card simply reappears next navigation */
    }
    setDismissed(true);
  };

  const neverAsk = () => {
    ReactGA.event("demographics_skipped", { wave: 2, surface: "card" });
    dispatch(updateDemographics({ declined: true }));
    setDismissed(true);
  };

  return (
    <aside className="aboutYouCard" aria-label="Optional questions about you">
      <div className="aboutYouCard__copy">
        <p className="aboutYouCard__title">
          Who is No Wasted Days actually for?
        </p>
        <p className="aboutYouCard__body">
          We genuinely don&rsquo;t know yet. Five optional questions would tell us
          who we&rsquo;re serving &mdash; and who we&rsquo;re missing. About twenty
          seconds, and it never touches your journal.
        </p>
      </div>
      <div className="aboutYouCard__actions">
        <button
          type="button"
          className="aboutYou__button"
          onClick={() => navigate("/about-you")}
        >
          Tell us
        </button>
        <button
          type="button"
          className="aboutYou__button aboutYou__button--quiet"
          onClick={notNow}
        >
          Not now
        </button>
        <button
          type="button"
          className="aboutYou__button aboutYou__button--plain"
          onClick={neverAsk}
        >
          Don&rsquo;t ask again
        </button>
      </div>
    </aside>
  );
}
